import React, { useState } from 'react';
import { setToken } from '../api';

const DEFAULT_API = 'https://trustflowwebsite-production.up.railway.app/api';

export default function LoginPage({ onLogin, apiBase = DEFAULT_API }) {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function callApi(path, body) {
    const res = await fetch(`${apiBase}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || data.details || data.error || 'Có lỗi xảy ra');
    return data;
  }

  async function handleSendOtp(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true); setError('');
    try {
      await callApi('/auth/otp', { email: email.trim() });
      setStep('otp');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleLogin(e) {
    e.preventDefault();
    if (!otp.trim()) return;
    setLoading(true); setError('');
    try {
      const data = await callApi('/auth/login', { email: email.trim(), otp: otp.trim() });
      const token = data.token || data.data?.token;
      if (!token) throw new Error('Không nhận được token');
      setToken(token);
      sessionStorage.removeItem('tf_auth_redirecting');

      // Decode payload for basic user info (no server call needed)
      let userInfo = { email: email.trim(), isPremium: false };
      try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        userInfo = { userId: String(payload.userId || ''), email: payload.email || email.trim(), isPremium: false };
      } catch { /* use fallback */ }

      onLogin(userInfo);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={s.page}>
      <div style={s.card}>
        {/* Logo / header */}
        <div style={s.header}>
          <div style={s.logoRow}>
            <span style={s.logoIcon}>🔍</span>
            <span style={s.logoText}>TrustFlow</span>
            <span style={s.logoSub}>Research</span>
          </div>
          <p style={s.tagline}>Phân tích dự án tài sản số/ tài sản mã hóa chuyên sâu</p>
        </div>

        {step === 'email' ? (
          <form onSubmit={handleSendOtp} style={s.form}>
            <label style={s.label}>Email đăng nhập TrustFlow</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="your@email.com"
              style={s.input}
              autoFocus
              required
            />
            {error && <p style={s.error}>{error}</p>}
            <button type="submit" style={s.btn} disabled={loading || !email.trim()}>
              {loading ? 'Đang gửi...' : 'Gửi mã OTP'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleLogin} style={s.form}>
            <div style={s.sentBanner}>
              ✓ Mã OTP đã gửi tới <strong>{email}</strong>
            </div>
            <label style={s.label}>Nhập mã OTP</label>
            <input
              type="text"
              value={otp}
              onChange={e => setOtp(e.target.value)}
              placeholder="6 chữ số"
              style={{ ...s.input, letterSpacing: '0.25em', textAlign: 'center', fontSize: 22 }}
              maxLength={8}
              autoFocus
              required
            />
            {error && <p style={s.error}>{error}</p>}
            <button type="submit" style={s.btn} disabled={loading || !otp.trim()}>
              {loading ? 'Đang xác thực...' : 'Đăng nhập'}
            </button>
            <button
              type="button"
              style={s.back}
              onClick={() => { setStep('email'); setError(''); setOtp(''); }}
            >
              ← Dùng email khác
            </button>
          </form>
        )}

        <p style={s.note}>
          Sử dụng tài khoản TrustFlow của bạn để truy cập Research.
        </p>
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg)',
    padding: 24,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    background: 'var(--surface)',
    borderRadius: 16,
    boxShadow: '0 4px 32px rgba(31,88,164,0.12)',
    border: '1px solid var(--border)',
    overflow: 'hidden',
  },
  header: {
    background: 'linear-gradient(135deg, #29a7df, #2657a5)',
    padding: '32px 32px 24px',
    textAlign: 'center',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 6,
  },
  logoIcon: { fontSize: 28 },
  logoText: {
    fontSize: 26,
    fontWeight: 700,
    color: '#ffffff',
    fontFamily: 'Be Vietnam Pro, sans-serif',
  },
  logoSub: {
    fontSize: 13,
    fontWeight: 500,
    color: 'rgba(255,255,255,0.75)',
    alignSelf: 'flex-end',
    paddingBottom: 3,
    fontFamily: 'Be Vietnam Pro, sans-serif',
  },
  tagline: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 13,
    margin: 0,
  },
  form: {
    padding: '28px 32px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text)',
  },
  input: {
    padding: '11px 14px',
    borderRadius: 8,
    border: '1.5px solid var(--border-mid)',
    background: 'var(--surface)',
    color: 'var(--text)',
    fontSize: 15,
    outline: 'none',
    fontFamily: 'Be Vietnam Pro, sans-serif',
    transition: 'border-color 0.2s',
  },
  btn: {
    padding: '12px 0',
    borderRadius: 8,
    border: 'none',
    background: 'linear-gradient(135deg, #29a7df, #2657a5)',
    color: '#fff',
    fontWeight: 600,
    fontSize: 15,
    cursor: 'pointer',
    fontFamily: 'Be Vietnam Pro, sans-serif',
    marginTop: 4,
  },
  back: {
    background: 'none',
    border: 'none',
    color: 'var(--tf-cyan)',
    fontSize: 13,
    cursor: 'pointer',
    padding: 0,
    textAlign: 'left',
  },
  error: {
    color: '#dc2626',
    background: 'rgba(220,38,38,0.07)',
    border: '1px solid rgba(220,38,38,0.2)',
    borderRadius: 6,
    padding: '8px 12px',
    fontSize: 13,
    margin: 0,
  },
  sentBanner: {
    background: 'rgba(41,167,223,0.08)',
    border: '1px solid rgba(41,167,223,0.2)',
    borderRadius: 6,
    padding: '9px 12px',
    fontSize: 13,
    color: 'var(--text)',
  },
  note: {
    padding: '16px 32px 24px',
    fontSize: 12,
    color: 'var(--text-muted)',
    textAlign: 'center',
    margin: 0,
  },
};
